package dev.danko.roblox_craft_ai.uikit.i18n

import dev.danko.roblox_craft_ai.shared.model.AssetType

object EnStrings : AppStrings {
    override val common = object : CommonStrings {
        override val cancel = "Cancel"
        override val save = "Save"
        override val delete = "Delete"
        override val retry = "Retry"
        override val regenerate = "Regenerate"
        override val close = "Close"
        override val clear = "Clear"
        override val copy = "Copy"
        override val copied = "Copied"
        override val confirm = "Confirm"
        override val send = "Send"
        override val show = "Show"
        override val hide = "Hide"
        override val help = "Help"
        override val selectPlaceholder = "Select..."
        override val demoBadge = "Demo"
        override val betaBadge = "Beta"
        override val you = "You"
        override val language = "Language"
        override val simpleMode = "Simple mode"
        override val expertMode = "Expert mode"
        override val simpleModeHint = "Simple mode hides the AI model picker and technical details"
        override fun lines(count: Int) = if (count == 1) "1 line" else "$count lines"
        override fun secondsShort(seconds: Int) = "${seconds}s"
        override fun minutesSeconds(minutes: Int, seconds: Int) = "${minutes}m ${seconds.toString().padStart(2, '0')}s"
        override fun agoSeconds(seconds: Int) = "${seconds}s ago"
        override fun agoMinutes(minutes: Int) = "${minutes}m ago"
    }

    override val assetTypes = object : AssetTypeStrings {
        override fun name(type: AssetType) = when (type) {
            AssetType.LUAU_SCRIPT -> "Luau script"
            AssetType.MESH_3D -> "3D model"
            AssetType.ANIMATION -> "Animation"
            AssetType.ROJO_PROJECT -> "Rojo project"
        }

        override fun generateButton(type: AssetType) = when (type) {
            AssetType.LUAU_SCRIPT -> "Create script"
            AssetType.MESH_3D -> "Create 3D model"
            AssetType.ANIMATION -> "Create animation"
            AssetType.ROJO_PROJECT -> "Create Rojo project"
        }

        override fun availabilityNote(type: AssetType) = when (type) {
            AssetType.MESH_3D -> "Beta: the 3D model is built from a sketch or your picture (paperclip). In Studio the model is inserted as a placeholder for now."
            AssetType.ANIMATION -> "Beta: AI creates an animation for an R15 character. Select an R15 rig before sending to Studio — it opens in the Animation Editor."
            else -> null
        }
    }

    override val errors = object : ErrorStrings {
        override val aiUnavailable = "The AI is unavailable right now. Try again in a minute."
        override val rateLimited = "Too many requests. Wait a little and try again."
        override val network = "Can't reach the server. Check that the backend is running."
        override val meshTimeout = "The 3D model took too long. Try again."
        override val meshFailed = "Couldn't create the 3D model. Try describing it differently."
        override val cancelled = "Generation cancelled."
        override val generic = "Something went wrong. Try again."
        override val details = "Details"
        override val backendDownTitle = "The server is not running"
        override val backendDownHint = "Nothing can be created while the backend is off. Start it in the project folder with ./gradlew :backend:run — this page will notice when it is back."
        override val checkAgain = "Check again"
        override val contentBlocked = "This request can't be done: it breaks the rules for Roblox games. Try describing your idea differently."
        override val unsafeCodeBlocked = "The AI wrote code that breaks the safety rules, so we didn't deliver it. Try rephrasing your request."
        override val authRequired = "Sign in with your email to create things — it takes a minute."
        override val quotaExceeded = "You're out of credits. Buy a credit pack or pick a plan on the 💎 Pricing page."
        override val budgetExceeded = "Today's generation limit has been reached. Try again tomorrow."
    }

    override val promptBox = object : PromptBoxStrings {
        override val label = "What shall we make?"
        override val attachReference = "Attach picture"
        override fun addMorePhotos(count: Int) = "Add picture ($count)"
        override val placeholder = "Describe what to create for Roblox. You can paste a picture (Ctrl+V / Cmd+V)..."
        override val referencesLabel = "🖼️ Example pictures:"
        override val assetTypeLabel = "What to create"
        override val aiModelLabel = "AI model"
        override val autoProvider = "🤖 Auto"
        override val generating = "Creating..."
        override val tooManyReferences = "⚠️ Up to three pictures. Extra files were skipped"
        override val viewsTitle = "Views"
        override val viewFront = "Front"
        override val viewBack = "Back"
        override val viewLeft = "Left"
        override val viewRight = "Right"
        override val viewsHint = "Upload a sheet with several views and it is cut automatically, or add views one by one. With 2 or more views the model is built from them directly, without a sketch."
        override val viewsSplitting = "Looking for views in the picture…"
        override fun viewsSplit(count: Int) = "Found $count views. Check that each is in the right slot"
        override val viewsSwapSides = "Swap left and right"
        override val viewsHighOnlyFront = "High (TRELLIS 2) uses the front view only. Standard and Maximum use all views."
        override val viewsSwitchToMax = "Switch to Maximum"
        override val viewsTooMany = "⚠️ The slots are full. Remove a picture to add a new one"
        override val onlyImages = "⚠️ Only PNG, JPG and WebP are supported"
        override val fileTooLarge = "⚠️ File is too large, 5 MB max"
        override val dropTitle = "+ Drop example pictures here"
        override val dropHint = "Up to 3 files, 5 MB each"
        override val dropAria = "Drop example pictures here, maximum three"
        override val emptyPrompt = "✏️ Describe what to create first"
        override fun promptFromImages(type: AssetType) = "Roblox ${assetTypes.name(type).lowercase()} based on the attached pictures"
    }

    override val assetCard = object : AssetCardStrings {
        override val stagePrompt = "Prompt"
        override val stageAi = "AI"
        override val stageBuild = "Build"
        override val stageSync = "Check"
        override val stageDone = "Done"
        override val statusPending = "Queued"
        override val statusValidating = "Checking"
        override val statusRetrying = "Fixing"
        override val statusStreaming = "Creating"
        override val statusReady = "Ready"
        override val statusFailed = "Failed"
        override fun revisionBadge(current: Int, total: Int) = "v$current ($total ${if (total == 1) "version" else "versions"})"
        override fun referencesBadge(count: Int) = "🖼️ $count"
        override fun syncPending(countdown: String) = "🔗 Waiting for Studio...$countdown"
        override fun syncDelivering(countdown: String) = "⚙ Sending...$countdown"
        override val syncSynced = "✅ In Studio!"
        override val syncFailed = "❌ Sending failed"
        override val deleteTooltip = "Delete"
        override val waitingForPlugin = "🔗 Waiting for the Roblox Studio plugin to pick it up..."
        override val progressValidating = "⚙ Checking the code..."
        override val progressRetrying = "⚠️ Found a mistake, fixing it..."
        override fun progressTokens(count: Int) = "⚡ Writing code... ($count)"
        override val progressProcessing = "⏳ Working..."
        override val download = "📦 Download: "
        override val editPrompt = "✏️ Edit prompt"
        override val aiChat = "AI chat"
        override val downloadZip = "Download .ZIP"
        override val synced = "Sent!"
        override val retrySync = "Send again"
        override val syncToStudio = "To Roblox Studio"
        override val demoMeshHint = "This is a demo model: a real 3D model is built from a sketch or a picture once a 3D generator is configured on the server."
        override val betaAnimationHint = "Animations are in beta: this is a demo result."
    }

    override val pipeline = object : PipelineStrings {
        override val prompt = "Prompt"
        override val luauPromptDescription = "Prompt received"
        override val llmStream = "AI writes code"
        override val llmStreamDescription = "The AI is writing Luau code"
        override val syntaxCheck = "Check"
        override val syntaxCheckDescription = "Syntax and safety check"
        override val ready = "Ready"
        override val luauReadyDescription = "Script can be sent to Studio"
        override val meshPromptDescription = "3D model description"
        override val meshGeneration = "3D generation"
        override val meshGenerationDescription = "Creating the model shape"
        override val meshTexturing = "Textures"
        override val meshTexturingDescription = "Colors and materials"
        override val meshExport = "GLB export"
        override val meshExportDescription = "Preparing the model file"
        override val meshReadyDescription = "3D model is ready"
        override val animationPromptDescription = "Animation description"
        override val motionAi = "Motion"
        override val motionAiDescription = "Creating keyframes"
        override val rigging = "R15 rig"
        override val riggingDescription = "Binding to R15 bones"
        override val animationReadyDescription = "Animation is ready (beta)"
        override val scaffoldTree = "Structure"
        override val scaffoldTreeDescription = "Creating Rojo project files"
        override val serverServices = "Server"
        override val serverServicesDescription = "Server services"
        override val clientControllers = "Client"
        override val clientControllersDescription = "Client controllers and UI"
        override val rojoReady = "Ready"
        override val rojoReadyDescription = "Project is ready for Rojo"
    }

    override val chat = object : ChatStrings {
        override val quickOptimize = "⚡ Make it faster"
        override val quickStrict = "🛡️ Enable --!strict and types"
        override val quickBugs = "🐛 Find bugs"
        override val quickErrors = "✨ Add error handling"
        override val quickDocs = "📝 Add comments"
        override fun revisionShort(number: Int) = "Version $number"
        override fun revisionLong(number: Int) = "Version #$number"
        override val inputPlaceholder = "Write what to change or improve in the script."
        override val aiAssistant = "🤖 AI helper"
        override val diffTitle = "Changes (diff)"
        override val acceptChanges = "Accept changes"
        override val thinking = "The AI is reading the code and making changes..."
        override val changesApplied = "✅ Changes applied"
    }

    override val code = object : CodeStrings {
        override val run = "Run"
        override val runViaBackend = "Run via backend…"
        override val connectPluginFirst = "Connect the Roblox Studio plugin first"
        override fun projectTree(files: Int) = "📁 Project files ($files)"
        override val filterPlaceholder = "Filter..."
        override val undoTooltip = "Undo last action"
        override val redoTooltip = "Redo"
        override val resetTooltip = "Restore original code"
        override val copyTooltip = "Copy code"
        override val downloadTooltip = "Download script as .lua"
        override val fullscreenTooltip = "Fullscreen"
    }

    override val meshViewer = object : MeshViewerStrings {
        override val shadingTextured = "Texture"
        override val shadingClay = "Clay"
        override val shadingWire = "Wire"
        override val shadingFlat = "Flat"
        override val lightStudio = "Studio"
        override val lightSun = "Sun"
        override val lightNeon = "Neon"
        override val cameraIso = "Iso"
        override val cameraFront = "Front"
        override val cameraTop = "Top"
        override val cameraSide = "Side"
        override val pause = "Pause"
        override val spin = "Spin"
        override val exitFullscreen = "Exit"
        override val fullscreen = "Full"
        override fun stats(triangles: String, vertices: String, dimensions: String) = "📊 $triangles Tris • $vertices Verts • $dimensions"
        override val controlsHint = "LMB: Orbit • RMB/Shift: Pan • Wheel: Zoom"
        override val screenshot = "Screenshot"
        override val screenshotTitle = "3D Screenshot"
        override fun screenshotSaved(filename: String) = "Model screenshot saved: $filename"
        override val animationParseError = "Couldn't read the animation, the character stays in its rest pose"
        override val animationMissing = "This work has no keyframes (old demo) — create the animation again"
        override val animationPreviewTitle = "Animation preview"
        override val play = "Play"
        override val stop = "Stop"
        override val loopOn = "Loop: ON"
        override val loopOff = "Loop: OFF"
        override val exportKeyframes = "Export keyframes (.json)"
        override fun resizeHint(heightPx: Int) = "↕ Drag to resize (${heightPx}px)"
        override val shadingBaseColor = "Colour only"
        override val shadingNormals = "Normals"
        override val shadingUvChecker = "UV checker"
        override val shadingXray = "X-ray"
        override val lightRoblox = "Roblox"
        override val lightNight = "Night"
    }

    override val header = object : HeaderStrings {
        override val appTitle = "Craftiva AI"
        override val appSubtitle = "Roblox Workshop"
        override val presets = "📚 Ideas"
        override val settings = "Settings"
        override fun aiReady(count: Int) = "AI: $count connected"
        override val aiDemo = "AI: demo mode"
        override val pluginConnected = "Plugin: connected"
        override val pluginPending = "Plugin: waiting"
        override val tokenLabel = "Token: "
    }

    override val home = object : HomeStrings {
        override val searchWorks = "Find a work"
        override val allIdeas = "All ideas →"
        override val open = "Open"
        override val moreActions = "More actions"
        override val generateSimilar = "Similar"
        override val options = "Options"
        override val sendToStudio = "Send to Roblox Studio"
        override fun creditsCost(credits: Int) = if (credits == 1) "1 credit" else "$credits credits"
        override val rename = "Rename"
        override val renameFailed = "Could not rename"
        override val noMatches = "Nothing found"
        override val pageTitle = "Craftiva AI — Roblox Workshop"
        override val heroTitle = "What shall we make today?"
        override val heroSubtitle = "Describe your idea — get a ready script or model and send it to Roblox Studio in one click."
        override val samplePrompt = "Make a wooden sword"
        override val savePreset = "💾 Save as idea"
        override val addToQueue = "✚ Add to queue"
        override val conceptMode = " 🎨 Sketch first, then 3D"
        override val shiftHint = "💡 Shift + Create = add to queue"
        override val noIdea = "💡 Not sure what to make?"
        override val browsePresets = "📚 Browse ideas"
        override val recentAssets = "My creations"
        override fun totalAssets(count: Int) = "($count total)"
        override val wideView = "Wide view"
        override val gridView = "Grid view"
        override val noAssetsTitle = "Nothing here yet"
        override val noAssetsHint = "Write a prompt above to make your first creation."
        override fun generationFailed(reason: String) = "Generation error: $reason"
        override val cancelledByUser = "Generation cancelled"
        override val conceptGenerating = "🎨 Drawing a sketch..."
        override val conceptReady = "✨ Sketch is ready! Check the shape before 3D."
        override fun conceptFailed(reason: String) = "Sketch error: $reason"
        override val conceptApproved = "🚀 Sketch approved! Creating the 3D model..."
        override fun meshFromConceptFailed(reason: String) = "3D model error: $reason"
        override val conceptRerolling = "🔄 Drawing another version..."
        override val conceptRerolled = "✨ New version is ready!"
        override fun conceptRerollFailed(reason: String) = "Couldn't redraw: $reason"
        override fun presetLoaded(title: String) = "✨ Idea “$title” loaded"
        override fun queued(pending: Int, position: Int) = "✨ In queue: $pending, position #$position"
        override val enterPromptBeforeSaving = "⚠️ Write a prompt first"
        override val personalPresetDescription = "My idea"
        override fun presetSaved(title: String) = "💾 Idea “$title” saved. Find it in 📚 Ideas → 👤 Mine"
        override val presetSaveFailed = "Couldn't save"
        override fun cardDeleted(shortId: String) = "🗑️ Creation #$shortId deleted"
        override val regenerating = "🔄 Creating again..."
        override fun queuedForStudio(shortId: String) = "Creation #$shortId queued for Studio!"
        override val syncBackendUnavailable = "Couldn't reach the server to send it."
        override val settingsSaved = "Settings saved!"
        override fun refactorFailed(reason: String) = "Code change error: $reason"
        override val changesApplied = "Changes applied!"
        override fun applyChangesFailed(reason: String) = "Couldn't apply changes: $reason"
    }

    override val presetDialog = object : PresetDialogStrings {
        override val title = "💾 Save as my idea"
        override val nameLabel = "Name"
        override val namePlaceholder = "My cool fire effect (3–60 characters)"
        override val nameTooShort = "At least 3 characters"
        override fun nameCounter(length: Int) = "OK · $length/60"
        override val descriptionLabel = "Description (optional)"
        override val descriptionPlaceholder = "What this prompt is for (up to 140 characters)"
        override val tagsLabel = "Tags (optional)"
        override val tagsPlaceholder = "fire, magic, sword — comma separated"
        override fun tagsCounter(count: Int) = "Up to 6 tags · $count"
        override val currentPrompt = "Current prompt (will be saved):"
        override val emptyPrompt = "(empty — write a prompt first)"
        override fun typeAndLimit(typeName: String) = "Type: $typeName · up to 50 personal ideas"
    }

    override val queue = object : QueueStrings {
        override val statusPending = "Waiting"
        override val statusRunning = "Running"
        override val statusDone = "Done"
        override val statusFailed = "Failed"
        override val statusCancelled = "Cancelled"
        override fun title(size: Int) = "📦 Queue ($size)"
        override fun eta(minutes: Int, seconds: Int) = " ⏱️ ~${minutes}m ${seconds.toString().padStart(2, '0')}s left"
        override val resume = "▶️ Resume"
        override val pause = "⏸️ Pause"
        override val resumed = "Queue resumed"
        override val paused = "Queue paused"
        override val pausedBadge = " ⏸️ Paused"
        override val cleared = "Finished tasks cleared"
        override val empty = "🕳️ No tasks yet. Shift + Create adds a task to the queue."
        override val scrollTip = "💡 Lists over 30 tasks scroll"
        override val taskCancelled = "Task cancelled"
        override fun position(position: Int) = "position $position"
        override val startCallbackMissing = "The queue is not ready yet"
        override val generationFailed = "Generation failed"
        override val unexpectedError = "Unexpected error"
    }

    override val concept = object : ConceptStrings {
        override val stepTitle = "1. Sketch (2D)"
        override val stepHint = "Check the shape and silhouette before creating the 3D model"
        override val savingBadge = "💎 Saves 3D generations"
        override val imageAlt = "Sketch"
        override fun promptExpansion(expanded: Boolean) = "🔍 Detailed prompt for the artist (${if (expanded) "collapse" else "show"})"
        override val reconstructing = "⏳ Creating 3D..."
        override val approve = "✅ Looks good, make 3D"
        override val regenerating = "↻ Drawing..."
        override val regenerate = "🔄 Draw another"
    }

    override val health = object : HealthStrings {
        override val unknown = "⚪ Status unknown"
        override val allOk = "🌿 All systems OK"
        override val critical = "🔴 Critical error"
        override fun degraded(component: String) = "🟡 Issues: $component"
        override val degradedGeneric = "🟡 Some issues"
        override val title = "System status"
        override fun updatedAt(time: String) = "Updated: $time"
    }

    override val sessionToken = object : SessionTokenStrings {
        override val validationFailed = "Couldn't validate the token. Make sure it matches the one in the Roblox Studio plugin."
        override val title = "🔗 Link the Roblox Studio plugin"
        override val saveAndLink = "Save & link"
        override val instructions = "💡 Copy this site's token and paste it into the Craftiva plugin window in Roblox Studio — then your creations go straight into Studio."
        override val tokenLabel = "Session token"
        override val tokenPlaceholder = "e.g. RK_LIVE_7AB3C9EF"
        override val pasteFromClipboard = "Paste from clipboard"
        override val generateNew = "Generate new"
    }

    override val syncModal = object : SyncModalStrings {
        override val untitledAsset = "(untitled)"
        override val title = "⚠️ Send to Roblox Studio?"
        override val confirm = "Yes, send"
        override val warning = "⚠️ It will appear in your Roblox Studio at the next plugin sync (every 3 seconds). Scripts are inserted disabled — review the code and press \"Enable\" in the plugin window. Everything can be undone with Ctrl+Z."
        override val averageDelay = "⏱️ Usually takes 2–3 seconds."
        override val dontShowAgain = "Don't ask again (not recommended)"
    }

    override val revisions = object : RevisionStrings {
        override val title = "👁️ Code & version history"
        override val listLabel = "Versions:"
        override fun restore(number: Int) = "Restore v$number"
        override fun restored(number: Int) = "Restored version v$number"
        override fun latest(number: Int) = "Latest (v$number)"
    }

    override val settingsPage = object : SettingsPageStrings {
        override val tabAccount = "👤 Account"
        override val signedOutTitle = "You're not signed in"
        override val signedOutHint = "Sign in with your email to see your plan and credits and use the service on any device."
        override val advanced = "Advanced"
        override val dangerZone = "Reset"
        override val resetSessionHint = "Deletes all keys and settings of this session. Works on the server stay."
        override val pageTitle = "Craftiva AI — Settings"
        override val subtitle = "Settings panel"
        override val hashLabel = "Hash: "
        override val backToStudio = "← Back to workshop"
        override val cabinetTitle = "⚙️ Settings"
        override val cabinetSubtitle = "Account, Roblox Studio plugin and preferences"
        override val resetSession = "Reset session"
        override val resetSessionConfirm = "Delete all keys and settings of this session?"
        override val sessionReset = "Session reset"
        override val tabKeys = "🔑 API keys"
        override val tabGeneral = "⚙️ General"
        override val tabPlugin = "🔌 Plugin"
        override val tabAssets = "📦 Creations"
        override val keyAdded = "Key added ✓"
        override val keyAddFailed = "Couldn't add the key"
        override val keyUpdated = "Key updated"
        override val keyUpdateFailed = "Couldn't update the key"
        override val keyDeleted = "Key deleted"
        override val keyDeleteFailed = "Couldn't delete the key"
        override val priorityChanged = "Priority changed"
        override val priorityChangeFailed = "Couldn't change priority"
        override val settingsSaved = "Settings saved ✓"
        override val saveFailed = "Couldn't save"
        override val resetDone = "Settings reset"
        override val resetFailed = "Couldn't reset"
        override val pluginSettingsSaved = "Plugin settings saved"
        override val connectionOkEmpty = "Connected (queue is empty)"
        override fun connectionOkQueued(count: Int) = "Connected ($count in queue)"
        override val connectionFailed = "Couldn't reach the sync server"
        override val assetRemovedFromCache = "Creation removed from browser cache"
        override val keysTitle = "API key management"
        override val keysManagedByServerTitle = "🔐 Keys are managed by the server"
        override val keysManagedByServerHint = "Provider keys are configured by the server administrator via environment variables or secrets. They cannot be changed from the interface."
        override fun keysSummary(total: Int, active: Int) = "Keys: $total · Active: $active"
        override val refresh = "Refresh"
        override val catalogMissing = "Provider catalog not loaded"
        override val catalogMissingHint = "Check that the backend is running and reachable"
        override val providersHeader = "🤖 AI & 3D providers"
        override val noModels = "(no models available — add a key)"
        override val generalTitle = "General preferences"
        override val defaultModel = "Default AI model"
        override val selectModel = "Select a model..."
        override val defaultAssetType = "Default creation type"
        override val skipSyncConfirm = "Don't ask before sending to Studio"
        override val skipSyncConfirmHint = "Ready creations are sent to Roblox Studio right away"
        override val themeSystem = "🌓 System"
        override val themeLight = "☀️ Light"
        override val themeDark = "🌙 Dark"
        override val themeLabel = "Theme"
        override val languageLabel = "Interface language"
        override val reset = "Reset"
        override val pluginSettingsTitle = "Roblox Studio plugin settings"
        override val pluginTokenTitle = "Session token for the plugin"
        override val tokenCopied = "Token copied"
        override val sessionHashTitle = "Session hash (backend storage ID)"
        override val pluginPort = "Plugin port"
        override val pluginPortHint = "The server address is set in the plugin window in Roblox Studio"
        override val autoConnect = "Connect automatically on start"
        override val autoConnectHint = "The plugin starts syncing as soon as Studio opens"
        override val studioPath = "Roblox Studio install path (optional)"
        override val testConnection = "Test connection"
        override val howToConnectTitle = "📚 How to connect the plugin?"
        override val howToConnectSteps = listOf(
            "1. Download the plugin with the button below — the server address is already set in it.",
            "2. Put the file into the Roblox Studio plugins folder (in Studio: Plugins → Plugins Folder) and restart Studio.",
            "3. Open the Craftiva AI panel, paste the session token from above and press “Save and connect”. Allow Studio to reach the server when asked.",
            "4. Finished works arrive in Studio disabled: check the code and press “Enable”. For animations, select an R15 rig first. Ctrl+Z undoes an insert."
        )
        override val downloadPlugin = "Download plugin"
        override val myAssetsTitle = "My creations (browser cache)"
        override fun assetsSummary(total: Int, shown: Int) = "Total: $total · Shown: $shown"
        override val filterAll = "All"
        override val filterReady = "Ready"
        override val filterProcessing = "In progress"
        override val filterFailed = "Failed"
        override val deselectAll = "Deselect all"
        override val selectAll = "Select all"
        override fun export(selected: Int) = if (selected > 0) "Export ($selected)" else "Export all"
        override fun deleteSelected(count: Int) = "Delete ($count)"
        override fun deleteAssetsConfirm(count: Int) = "Delete $count creation(s) from cache?"
        override val deleted = "Deleted"
        override val noAssets = "No creations yet"
        override val noAssetsHint = "Make your first creation on the main page"
        override val goToStudio = "Go to workshop →"
    }

    override val providerCard = object : ProviderCardStrings {
        override val defaultLabel = "Default"
        override fun type(name: String) = "Type: $name"
        override val active = "✓ Active"
        override val notConfigured = "Not configured"
        override fun addKeyFor(provider: String) = "+ Add key for $provider"
        override fun addKeyTitle(provider: String) = "Add $provider key"
        override val labelField = "Key name"
        override val labelPlaceholder = "Default, Work, etc."
        override val apiKeyField = "API key"
        override val apiKeyPlaceholder = "Paste the API key..."
        override fun keyPatternWarning(provider: String) = "⚠️ $provider keys usually look different"
        override val keyTooShort = "Key is too short (min 8 characters)"
        override val baseUrlOptional = "Base URL (optional)"
        override fun baseUrlDefault(url: String) = "Default: $url"
        override val test = "Test"
        override val getApiKey = "Get an API key ↗"
        override fun editKeyTitle(provider: String) = "Edit $provider key"
        override val labelShort = "Name"
        override val baseUrlEmptyDefault = "Base URL (empty = default)"
        override val keyHiddenNotice = "🔒 The key is stored and not shown. To replace it, create a new key."
        override fun deleteKeyTitle(label: String) = "Delete key “$label”?"
        override val deleteIrreversible = "This can't be undone. The key will be permanently removed."
        override fun providerKeyLine(provider: String, key: String) = "⚠️ Provider: $provider · Key: $key"
        override fun secondsLeft(seconds: Int) = "(${seconds}s)"
    }

    override val presetsPage = object : PresetsPageStrings {
        override val pageTitle = "Craftiva AI — Ideas"
        override val subtitle = "📚 Idea library"
        override val toStudio = "🎮 To workshop"
        override val categories = "Categories"
        override val categoryAll = "All"
        override val categoryEnvironment = "Environment"
        override val categoryNpc = "Characters & NPCs"
        override val categoryInterior = "Interior & furniture"
        override val categoryWeapon = "Weapons & tools"
        override val categoryItem = "Items"
        override val categoryAnimation = "Animations & effects"
        override val categoryScript = "Scripts"
        override val categoryPersonal = "Mine"
        override val categoryOther = "Other"
        override val allCategories = "All categories"
        override val difficultyEasy = "Easy"
        override val difficultyMedium = "Medium"
        override val difficultyHard = "Hard"
        override val searchPlaceholder = "🔍 Search by name, description, tags..."
        override fun found(count: Int) = "Found: $count"
        override val noPersonalTitle = "You don't have your own ideas yet"
        override val noPersonalHint = "Save a good prompt on the main page with the “💾 Save as idea” button."
        override val goToStudio = "🎮 Go to workshop"
        override fun nothingFound(query: String) = "Nothing found for “$query”"
        override val nothingFoundHint = "Try another search or pick a different category."
        override val deleteTitle = "Delete idea?"
        override fun deleted(title: String) = "Idea “$title” deleted"
        override fun deleteConfirm(title: String) = "Idea “$title” will be deleted permanently. Continue?"
        override val usePrompt = "Use"
    }

    override val nav = object : NavStrings {
        override val menu = "Menu"
        override val on = "On"
        override val off = "Off"
        override fun plan(name: String) = "$name plan"
        override val studioConnected = "Roblox Studio plugin connected"
        override val studioNotConnected = "Roblox Studio plugin not connected"
        override val workshop = "🎮 Workshop"
        override val home = "Home"
        override val about = "About"
        override val toggleColorMode = "Toggle color mode"
        override val builtWith = "Built with "
        override val templateBy = ", template designed by "
    }

    override val settingsRedirect = object : SettingsRedirectStrings {
        override val deprecatedNotice = "Use the /settings page instead of this dialog."
        override val redirecting = "Redirecting..."
        override val openingNewPage = "Opening the settings page"
        override val movedNotice = "Settings moved to a separate /settings page"
        override val openSettings = "Open settings →"
    }

    override val report = object : ReportStrings {
        override val button = "Report"
        override val title = "🚩 Report this item"
        override val hint = "Tell us what's wrong. An administrator will review the report."
        override val reasonLabel = "Reason"
        override val reasonInappropriate = "Inappropriate content"
        override val reasonUnsafeCode = "Dangerous or harmful code"
        override val reasonCopyright = "Copyright violation"
        override val reasonOther = "Other"
        override val commentLabel = "Comment (optional)"
        override val commentPlaceholder = "What exactly is wrong?"
        override val submit = "Send report"
        override val thanks = "Thank you! Your report has been sent."
        override val failed = "Couldn't send the report. Try again later."
    }

    override val auth = object : AuthStrings {
        override val signIn = "Sign in"
        override val signOut = "Sign out"
        override val signedOut = "You've signed out"
        override val accountTooltip = "Account"
        override val dialogTitle = "Sign in with email"
        override val dialogHint = "We'll email you a sign-in link. No password needed."
        override val emailLabel = "Email"
        override val emailPlaceholder = "you@example.com"
        override val sendLink = "Send link"
        override val sentTitle = "📬 Check your email"
        override fun sentHint(email: String) = "We sent a link to $email. It works for 15 minutes. If you can't find it, check your spam folder."
        override val useAnotherEmail = "Use another email"
        override val invalidEmail = "Check the address: it looks like there's a typo."
        override val throttled = "Too many emails. Wait 15 minutes and try again."
        override val sendFailed = "Couldn't send the email. Try again later."
        override val verifyTitle = "Signing in to Craftiva AI"
        override val verifying = "Checking the link…"
        override fun verifySuccess(email: String) = "Done! You're signed in as $email."
        override val verifyFailed = "This link doesn't work: it has expired or was already used."
        override val requestNewLink = "Request a new link"
        override val toWorkshop = "To the workshop →"
        override fun creditsTooltip(left: Int, limit: Int, purchased: Int, resetsOn: String) = "Monthly: $left of $limit (renews on $resetsOn). Purchased: $purchased"
    }

    override val billing = object : BillingStrings {
        override val navPricing = "💎 Pricing"
        override val pageTitle = "Craftiva AI — Pricing"
        override val title = "Plans and credits"
        override val subtitle = "Every action costs credits: a script is 1, a 3D model is 10. Monthly credits renew every month, purchased credits never expire."
        override val perMonth = "/mo"
        override fun creditsPerMonth(credits: Int) = "$credits credits per month"
        override val unlimitedCredits = "Unlimited"
        override val currentPlan = "Your plan"
        override val subscribe = "Subscribe"
        override val switchPlan = "Switch"
        override val free = "Free"
        override val packsTitle = "Credit packs"
        override val packsHint = "One-time purchase. Credits never expire and are used after your monthly credits."
        override fun packCredits(credits: Int) = "$credits credits"
        override val buy = "Buy"
        override val manageTitle = "Manage subscription"
        override val cancelSubscription = "Cancel subscription"
        override val updatePayment = "Update payment method"
        override fun renewsOn(date: String) = "Renews on $date"
        override val pastDueHint = "We couldn't charge your payment method. Update it to keep your plan."
        override val paymentSoon = "Payments are coming soon"
        override val signInToBuy = "Sign in to buy"
        override val checkoutDone = "Payment complete! Your plan and credits will update in a few seconds."
        override val checkoutFailed = "Couldn't open the checkout. Try again later."
        override val creditCostsTitle = "What actions cost"
        override fun balance(monthlyLeft: String, purchased: Int) = "Left: $monthlyLeft monthly + $purchased purchased"
    }

    override val admin = object : AdminStrings {
        override val nav = "🛠 Admin"
        override val pageTitle = "Craftiva AI — Admin"
        override val title = "Admin"
        override val tabOverview = "📊 Overview"
        override val tabPlans = "💳 Plans"
        override val tabPacks = "⚡ Packs"
        override val tabUsers = "👥 Users"
        override val tabModeration = "🛡️ Moderation"
        override val tabAudit = "📜 Audit log"
        override val days7 = "7 days"
        override val days30 = "30 days"
        override val totalCost = "Total spend"
        override val anonymousCost = "Anonymous"
        override val topUsers = "Top users by cost"
        override val byDay = "By day"
        override val events = "events"
        override val noData = "No data yet"
        override val name = "Name"
        override val monthlyCredits = "Credits per month"
        override val credits = "Credits"
        override val price = "Price (cents)"
        override val currency = "Currency"
        override val paddlePriceId = "Paddle Price ID"
        override val active = "Active"
        override val unlimitedHint = "Empty = unlimited"
        override val save = "Save"
        override val saved = "Saved"
        override val priceWarning = "The amount charged comes from the Paddle price. To change it, create a new price in Paddle and paste its Price ID here."
        override val addPack = "Add pack"
        override val packId = "Pack ID (a-z, 0-9, _)"
        override val searchUsers = "Search by email…"
        override val email = "Email"
        override val plan = "Plan"
        override val creditsMonth = "Credits this month"
        override val purchased = "Purchased"
        override val cost30d = "$ in 30 days"
        override val status = "Status"
        override val registered = "Signed up"
        override val lastLogin = "Last sign-in"
        override val creditsDelta = "Credits (+ add / − remove)"
        override val note = "Note"
        override val grant = "Apply"
        override val granted = "Credits updated"
        override val block = "Block"
        override val unblock = "Unblock"
        override val statusChanged = "Status changed"
        override val noUsers = "No users found"
        override val onlyOpen = "Open only"
        override val resolve = "Reviewed"
        override val resolved = "Marked as reviewed"
        override val kind = "Type"
        override val category = "Category"
        override val excerpt = "Excerpt"
        override val noIncidents = "No incidents"
        override val whenLabel = "When"
        override val admin = "Who"
        override val action = "Action"
        override val target = "Target"
        override val day = "Day"
        override val user = "User"
        override val cost = "Cost"
        override val providers = "Providers"
        override val details = "Details"
        override val totalEvents = "Total events"
        override fun saveFailed(reason: String) = "Could not save: $reason"
    }

    override val panel = object : AssetPanelStrings {
        override val tabCode = "Code"
        override val tabChat = "AI chat"
        override val tabVersions = "Versions"
        override val tabRequest = "Prompt"
        override val tabFiles = "Files"
        override val tab3d = "3D"
        override val tabPrep = "Setup script"
        override val tabSketch = "Sketch"
        override val tabAnimation = "Animation"
        override val downloadGlb = "Download .glb"
        override val downloadLua = "Download .lua"
        override val validationOk = "Checked: no errors"
        override val technical = "Technical details"
        override val whatToChange = "What should change? E.g.: close the door after 3 seconds"
        override val change = "Change"
        override val currentVersion = "Current"
        override val references = "Reference pictures"
        override val noVersions = "Only one version so far. New ones appear after edits in the chat."
        override fun version(n: Int) = "version $n"
        override fun validationIssues(n: Int) = "Issues: $n"
    }

    override val tour = object : TourStrings {
        override val step1Title = "Describe your idea"
        override val step1Text = "Write what you need: a script, a 3D model or an animation. You can attach an example picture with the paperclip."
        override val step2Title = "Get the result"
        override val step2Text = "The AI makes it in a few seconds. Open it to see the code or the model and ask for changes."
        override val step3Title = "Send it to Roblox Studio"
        override val step3Text = "Connect the plugin in Settings, and finished works will appear in Studio with one click."
        override val next = "Next"
        override val back = "Back"
        override val start = "Get started"
        override val skip = "Skip"
        override fun stepOf(step: Int, total: Int) = "Step $step of $total"
    }

    override val legal = object : LegalStrings {
        override val terms = "Terms of Service"
        override val privacy = "Privacy"
        override val refunds = "Refunds"
        override val notAffiliated = "Not affiliated with Roblox Corporation. Roblox is a trademark of Roblox Corporation."
        override val loginConsent = "By signing in you accept the Terms of Service and Privacy Policy. You must be 13 or older to use the service."
        override val checkoutConsent = "Purchases are for adults (18+) only. Payments are processed by Paddle. By paying you accept the Terms of Service and Refund Policy."
        override val backToSite = "← Back to site"
    }

    override val access = object : AccessStrings {
        override val badge = "Closed beta"
        override val title = "Build Roblox games with AI"
        override val subtitle = "Describe your idea in words — Craftiva AI writes a Luau script, makes a 3D model or animation and sends it to Roblox Studio. We are testing the service with a small group of users."
        override val f1Title = "Luau scripts"
        override val f1Text = "Doors, weapons, shops, damage systems — ready, checked code from your description."
        override val f2Title = "3D models"
        override val f2Text = "Draw a concept or describe an item — get a model ready for Roblox."
        override val f3Title = "Right into Studio"
        override val f3Text = "The Craftiva plugin puts the result into your game in one click."
        override val formTitle = "Request access"
        override val formHint = "Leave a request — we'll open access and email you when you can sign in."
        override val nameLabel = "Your name"
        override val namePlaceholder = "Name or nickname"
        override val messageLabel = "What do you want to build"
        override val messagePlaceholder = "For example: my own obby with traps and a shop"
        override val ageConfirm = "I am 13 or older (if under 16 and living in the EU — with a parent's consent)"
        override val privacyAccept = "I accept the Privacy Policy"
        override val submit = "Send request"
        override val sentTitle = "Request sent!"
        override fun sentText(email: String) = "We'll write to $email as soon as access is open. It usually takes 1–2 days."
        override val alreadyApproved = "Already approved?"
        override val signIn = "Sign in"
        override val notApproved = "This email has no access yet. Leave a request on the service page — we'll email you once it's approved."
        override val consentRequired = "Confirm your age and accept the privacy policy"
        override val sendFailed = "The request could not be sent. Please try again."
        override val adminTab = "Requests"
        override val pending = "Pending"
        override val approved = "Approved"
        override val rejected = "Rejected"
        override val all = "All"
        override val approve = "Approve"
        override val reject = "Reject"
        override val noRequests = "No requests"
        override val approvedToast = "Access granted, email sent"
        override val rejectedToast = "Request rejected"
    }

    override val meshInspector = object : MeshInspectorStrings {
        override val reliefMap = "Relief map"
        override val reliefYes = "yes — fine detail catches the light"
        override val reliefNo = "no"
        override val relief = "Relief"
        override val reliefHint = "Fine detail of the detailed model, kept in a texture (as Roblox will show it)"
        override fun number(value: Int) = groupDigits(value, ",")
        override val tabInfo = "Info"
        override val tabView = "View"
        override val tabTools = "Tools"
        override fun studs(value: Double): String {
            val n = shortDecimal(value, comma = false)
            return if (n == "1") "1 stud" else "$n studs"
        }
        override val readinessTitle = "Roblox readiness"
        override val readyAll = "Ready for Roblox"
        override fun readyIssues(count: Int) = if (count == 1) "1 thing to fix" else "$count things to fix"
        override fun checkTriangles(count: Int, max: Int) = "Triangles: ${groupDigits(count, ",")} of ${groupDigits(max, ",")}"
        override val checkTrianglesOver = "Over the MeshPart limit — Studio will reject the model"
        override fun checkTexture(size: Int) = "Texture ${size}×$size"
        override val checkTextureNone = "No texture — colours are stored in the vertices"
        override val checkTextureOver = "Roblox will shrink the texture to 1024 — better to shrink it now"
        override val checkFrontAuto = "The model's front was turned to Roblox's front (−Z) automatically"
        override val checkFrontUser = "You chose the model's front"
        override val checkFrontUnsure = "Not sure where the front is — check the Front view"
        override fun checkSize(dimensions: String) = "Size: $dimensions"
        override val checkSizeTiny = "Very small — hard to see in a game"
        override val checkSizeHuge = "Very big — make sure that is what you want"
        override val checkPartsOk = "One solid model, no loose bits"
        override fun checkParts(small: Int) = "Loose bits: $small — looks like generation debris"
        override val checkPivotOk = "Stands on the floor, pivot at the bottom"
        override val checkPivotOff = "Not on the floor — in Studio it will float or sink"
        override val checking = "Checking…"
        override val fixSimplify = "Simplify"
        override val fixTexture = "Shrink"
        override val fixRotate = "Turn"
        override val fixSize = "Set size"
        override val fixDebris = "Remove debris"
        override val fixPivot = "Put on the floor"
        override val geometry = "Geometry"
        override val triangles = "Triangles"
        override val vertices = "Vertices"
        override val parts = "Separate parts"
        override val size = "Size"
        override val fileSize = "File"
        override fun megabytes(bytes: Long) = if (bytes < 1024 * 1024) "${shortDecimal(bytes / 1024.0, false)} KB" else "${shortDecimal(bytes / 1048576.0, false)} MB"
        override val texture = "Texture"
        override fun pixels(size: Int) = "${size}×$size px"
        override val noTexture = "none"
        override val origin = "Where it came from"
        override val provider = "Generator"
        override val created = "Created"
        override val version = "Version"
        override val sketch = "Sketch"
        override val mode = "Mode"
        override val light = "Light"
        override val background = "Background"
        override val bgDark = "Dark"
        override val bgLight = "Light"
        override val bgGrey = "Grey"
        override val bgBaseplate = "Baseplate"
        override val reference = "R15 avatar next to it"
        override val referenceHint = "Compare the model with a regular Roblox character (5.2 studs)"
        override val bounds = "Dimensions in studs"
        override val grid = "Floor grid"
        override val spin = "Spin"
        override val screenshot = "Screenshot"
        override val screenshotTransparent = "No background (PNG)"
        override val video = "Spin video"
        override val videoRecording = "Recording the video…"
        override val videoUnsupported = "This browser can't record video"
        override val toolsNote = "Every change is saved as a new version — you can always go back."
        override val budget = "Triangle count"
        override val budgetMobile = "Phones"
        override val budgetNormal = "Normal"
        override val budgetDetailed = "Detailed"
        override fun trianglesValue(count: Int) = "${groupDigits(count, ",")} tris"
        override val apply = "Apply"
        override val sizeTitle = "Size in studs"
        override val sizeHint = "The model's longest side"
        override val rotateTitle = "Turn the front"
        override val rotateHint = "If the model faces sideways or backwards in Studio"
        override val rotateLeft = "↺ 90°"
        override val rotateRight = "↻ 90°"
        override val rotate180 = "180°"
        override val textureTitle = "Texture size"
        override val debrisTitle = "Remove loose bits"
        override val debrisHint = "Small floating pieces the generator left behind"
        override val downloadTitle = "Download"
        override val downloadGlb = "GLB"
        override val downloadObj = "OBJ + texture"
        override val versions = "Versions"
        override fun versionLabel(number: Int) = "v$number"
        override val current = "current"
        override val restore = "Restore"
        override val compare = "Compare"
        override val closeCompare = "Close comparison"
        override val originalVersion = "As generated"
        override fun editTriangles(count: Int) = "mesh at ${groupDigits(count, ",")} tris"
        override val editHighPoly = "detailed model (High-Poly)"
        override fun budgetFromSource(sourceTriangles: Int?) = "The generator's detailed model${sourceTriangles?.let { " (" + groupDigits(it, ",") + " tris)" } ?: ""} is kept for 30 days after creation: the triangle count can go down and up."
        override val downloadHighPoly = "High-Poly (GLB)"
        override val downloadSharp = "GLB · 2K texture"
        override val rigTitle = "Skeleton for animation"
        override val rigHint = "Puts 15 bones like R15 into the model (free). Works best for upright figures. Then pick the model as the character in the animation settings."
        override val rigApply = "Add bones"
        override val rigDone = "Bones added: the model can be picked as an animation character."
        override val rigDownload = "GLB with skeleton"
        override val rigWorking = "Adding bones…"
        override fun rigPro(cost: String?) = "Finer — Meshy" + (cost?.let { " · $it" } ?: "")
        override val rigProHint = "Meshy places 24 bones and spreads the skin more neatly; humanoids only, about a minute."
        override val rigByMeshy = "Bones by Meshy"
        override val preparingSharp = "Preparing the model with a 2K texture, this takes up to a minute."
        override val retextureTitle = "Change texture"
        override val retextureHint = "Shape and size stay, colours and material change. Takes 1–3 minutes and makes a new version. Describe the style or add a style picture."
        override val retexturePresets = listOf("Gold", "Stone", "Wood", "Rusty metal", "Cartoon, bright colours", "Ice")
        override val retexturePlaceholder = "For example: golden knight armour with red details"
        override val retexturePicture = "Style picture"
        override val retextureRemovePicture = "Remove picture"
        override fun retextureApply(cost: String?) = "Repaint" + (cost?.let { " · $it" } ?: "")
        override val retextureWorking = "Repainting the model, this takes 1–3 minutes"
        override val editRetexture = "new texture"
        override fun editSize(studs: String) = "size $studs"
        override fun editRotate(degrees: Int) = "turned $degrees°"
        override fun editTexture(size: Int) = "texture $size px"
        override val editDebris = "debris removed"
        override val working = "Changing the model… this can take up to a minute"
        override fun saved(number: Int) = "Done — saved as version $number"
        override val failed = "Couldn't change the model. Try again."
        override val unavailable = "Model tools are not connected on this server yet"
    }

    override val animationSetup = object : AnimationSetupStrings {
        override val characterTitle = "Character"
        override val characterR15 = "Standard R15"
        override val characterR15Hint = "the usual Roblox avatar"
        override val characterCustom = "Own character"
        override val characterCustomHint = "Own character: open a 3D work → Tools → Add bones."
        override val characterOwnHint = "my 3D work with bones"
        override val modeTitle = "Mode"
        override val modeNone = "Regular"
        override val modeNoneHint = "dances, walks, emotes"
        override val modeFists = "Fists"
        override val modeFistsHint = "punches, blocks, dodges"
        override val modeOne = "One weapon"
        override val modeOneHint = "sword, gun, staff, axe"
        override val modeTwo = "Two weapons"
        override val modeTwoHint = "combos with both hands"
        override val weaponTitle = "Weapon (right hand)"
        override val offhandTitle = "Left hand"
        override val sword = "Sword"
        override val gun = "Gun"
        override val staff = "Staff"
        override val axe = "Axe"
        override fun weaponHint(weapon: String) = when (weapon) { "GUN" -> "aim and recoil"; "STAFF" -> "long sweeps"; "AXE" -> "heavy, with a wind-up"; else -> "quick strikes" }
        override val weaponModelTitle = "Weapon model"
        override val weaponModelBuiltIn = "Built-in"
        override val weaponModelBuiltInHint = "simple shape"
        override val weaponModelOwnHint = "my 3D work"
        override val gripTitle = "Grip"
        override val gripOne = "One hand"
        override val gripOneHint = "the other hand is free"
        override val gripTwo = "Two hands"
        override val gripTwoHint = "both hands on the handle"
        override val note = "Moves follow the weapon's weight: heavy ones wind up and carry through. The weapon itself will show in the preview later."
        override val noteWithWeapon = "Moves follow the weapon’s weight: heavy ones wind up and carry through. The preview shows the weapon in hand; in Studio the plugin attaches a stand-in to the hand."
        override val openWorkspace = "Workspace"
        override val closeWorkspace = "Close"
        override val clipTitle = "Clip"
        override val duration = "Length"
        override val looped = "looped"
        override val once = "once"
        override val keys = "Keys"
        override val jointsTitle = "Joints now"
        override val prevKey = "Previous key"
        override val nextKey = "Next key"
        override val zoomIn = "Zoom in"
        override val zoomOut = "Zoom out"
        override val resetCamera = "Reset camera"
        override val timeZoom = "Time scale"
        override val cameraHint = "Wheel to zoom · left button to orbit · right button or Shift to pan"
        override val head = "Head"
        override val torso = "Torso"
        override val leftArm = "Left arm"
        override val rightArm = "Right arm"
        override val leftLeg = "Left leg"
        override val rightLeg = "Right leg"
        override val editTitle = "Change the animation"
        override val editHint = "Describe what to change, the rest stays as it is. Every change is saved as a version."
        override val editPresets = listOf("Wider arm moves", "Faster", "Slower", "Smoother", "Higher jump", "Return to the start pose faster")
        override val editPlaceholder = "For example: make the sword swing sharper with a bigger wind-up"
        override fun editApply(cost: String?) = "Change" + (cost?.let { " · $it" } ?: "")
        override val editWorking = "Changing the animation…"
        override fun editDone(version: Int) = "Done: version $version"
        override val editFailed = "Could not change it"
        override val versionsTitle = "Versions"
        override val versionOriginal = "original"
        override val restore = "Restore"
        override val libraryOpen = "Choose model…"
        override val libraryTitle = "Character and weapon model"
        override val libraryDone = "Done"
        override val libraryCharacterHint = "A character needs bones — an uploaded model gets them by itself. You can upload your own model below."
        override val libraryWeaponHint = "The model the character holds. Built-in is the sword, gun, staff or axe of the mode."
        override val libraryNoBones = "no bones"
        override val libraryEmpty = "Your 3D works and uploaded models will show up here."
        override val libraryAddBones = "Add bones (free)"
        override val libraryAddingBones = "Adding bones…"
        override val libraryTools = "Model tools"
        override val libraryDownload = "Download .glb"
        override val libraryUpload = "Upload your own model"
        override val libraryUploadHint = ".glb, or .obj with its .mtl and textures, up to 30 MB. Click or drop the files here."
        override val libraryUploading = "Uploading and preparing the model…"
        override val libraryFailed = "Something went wrong"
        override fun libraryImported(name: String) = "Model “$name” uploaded"
        override val weaponTitleOne = "Weapon — built-in or from “My works”"
        override val rightHandTitle = "Right hand"
        override val leftHandTitle = "Left hand"
        override val handTitle = "Hand"
        override val handLeadTitle = "Leading hand"
        override val handRight = "Right"
        override val handLeft = "Left"
        override val handRightHint = "The weapon in the right hand"
        override val handLeftHint = "The weapon in the left hand: the moves are mirrored"
        override fun weaponMovesLike(kind: String) = "moves like ${kind.lowercase()}"
    }

    override val quality = object : QualityStrings {
        override val sketchTitle = "Sketch"
        override val meshTitle = "3D model"
        override val sketchStandard = "Fast"
        override val sketchStandardHint = "cheap, may add things you didn't ask for"
        override val sketchHigh = "Accurate"
        override val sketchHighHint = "follows your description much better"
        override val meshStandard = "Standard"
        override val meshStandardHint = "≈30 s, for trying out and simple items"
        override val meshHigh = "High"
        override val meshHighHint = "≈1 min, cleaner shape and texture"
        override val meshMax = "Maximum"
        override val meshMaxHint = "≈3 min, the most detail"
        override val note = "Higher quality costs more credits. Pick what the job needs — standard is fine for trying out."
        override fun approve(cost: String) = "Make 3D · $cost"
        override val drewCheaper = "The accurate sketch model is unavailable right now — the fast one drew it and you were charged the fast price"
        override val variantsTitle = "How many sketches"
        override val variantsOne = "1"
        override val variantsOneHint = "when the idea is already clear"
        override val variantsFour = "4 to choose from"
        override val variantsFourHint = "drawn at once, you pick the best"
        override val pickVariant = "Click the sketch to make the 3D model from"
        override fun variantLabel(number: Int) = "Variant $number"
        override val trianglesTitle = "Polygons (triangles)"
        override val trianglesMob = "Mob / decor"
        override val trianglesCharacter = "Player / character"
        override val trianglesBoss = "Boss / large object"
        override val trianglesHighPoly = "High-Poly"
        override val trianglesHighPolyHint = "no limit, for download; Roblox takes up to 20,000"
        override val trianglesCustom = "Custom"
        override fun trianglesUpTo(count: Int) = "up to ${groupDigits(count, ",")}"
        override val trianglesNote = "A Roblox MeshPart takes up to 20,000 triangles. More triangles keep more small details but make the model heavier for the game."
        override val textureTitle = "Texture"
        override val texture1kHint = "1024 px, as in Roblox"
        override val texture2kHint = "2048 px, sharper in the downloaded model"
        override val texture4kHint = "4096 px, on High, in the High-Poly model"
        override val textureNote = "Roblox always gets a 1024 px texture: Studio scales bigger ones down. 2K and 4K are for downloads and other tools."
    }

    override val accountData = object : AccountDataStrings {
        override val title = "Your data"
        override val hint = "Download everything the service stores about you, or delete your account for good."
        override val download = "Download my data"
        override val delete = "Delete account"
        override val deleteTitle = "Delete your account for good?"
        override val deleteWarning = "Your account, all your works, their versions, 3D models and sketches will be deleted. Unused credits are lost. This cannot be undone."
        override val confirmLabel = "Type your email to confirm"
        override val confirmButton = "Delete for good"
        override val emailMismatch = "The email does not match this account"
        override val subscriptionActive = "Cancel your subscription on the Pricing page first, then delete the account."
        override val deleted = "Account deleted"
        override val failed = "The account could not be deleted. Please try again."
        override val adminDelete = "Delete account"
        override val adminDeleted = "Account deleted"
        override fun adminConfirm(email: String) = "Delete the account $email with all its works? This cannot be undone."
    }
}
